import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Payouts = () => {
  const [organizations, setOrganizations] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    fee: '0',
    paymentMethod: '',
    reference: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [orgsRes, payoutsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/admin/payouts/earnings?hasOutstanding=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/payouts?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setOrganizations(orgsRes.data.data || []);
      setPayouts(payoutsRes.data.data?.payouts || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'UGX') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'UGX',
    }).format(amount);
  };

  const handlePayoutClick = (org) => {
    setSelectedOrg(org);
    setPayoutForm({
      amount: org.payouts.outstanding.toString(),
      fee: '0',
      paymentMethod: '',
      reference: '',
      notes: '',
    });
    setShowPayoutModal(true);
  };

  const handleSubmitPayout = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/payouts`,
        {
          organizationId: selectedOrg.id,
          amount: parseFloat(payoutForm.amount),
          fee: parseFloat(payoutForm.fee),
          paymentMethod: payoutForm.paymentMethod,
          reference: payoutForm.reference,
          notes: payoutForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Payout created successfully!');
      setShowPayoutModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create payout');
    } finally {
      setSubmitting(false);
    }
  };

  const netAmount = parseFloat(payoutForm.amount || 0) - parseFloat(payoutForm.fee || 0);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <h1 className="h3 mb-1" style={{ fontWeight: '600' }}>Payouts</h1>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Manage organization payouts and view history
        </p>
      </div>

      {/* Organizations with Outstanding Balance */}
      <div className="card mb-4">
        <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>
            Organizations with Outstanding Balance
          </h5>
        </div>
        <div className="card-body">
          {organizations.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-check-circle" style={{ fontSize: '2rem', color: '#10b981' }}></i>
              <p className="text-muted mt-2 mb-0">All organizations paid up!</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th className="text-end">Total Earned</th>
                    <th className="text-end">Paid Out</th>
                    <th className="text-end">Outstanding</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.id}>
                      <td>
                        <div style={{ fontWeight: '500' }}>{org.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {org.earnings.voteCount} votes • {org.earnings.packageCount} packages
                        </div>
                      </td>
                      <td className="text-end">{formatAmount(org.earnings.totalEarned, org.currency)}</td>
                      <td className="text-end">
                        {formatAmount(org.payouts.totalPaidOut, org.currency)}
                        {org.payouts.totalFees > 0 && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Fees: {formatAmount(org.payouts.totalFees, org.currency)}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.875rem' }}>
                          {formatAmount(org.payouts.outstanding, org.currency)}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handlePayoutClick(org)}
                        >
                          Make Payout
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payout History */}
      <div className="card">
        <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Payout History</h5>
        </div>
        <div className="card-body">
          {payouts.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <p>No payouts yet</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Organization</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Fee</th>
                    <th className="text-end">Net Paid</th>
                    <th>Method</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id}>
                      <td style={{ fontSize: '0.875rem' }}>
                        {new Date(payout.createdAt).toLocaleDateString()}
                      </td>
                      <td>{payout.organization?.name || 'N/A'}</td>
                      <td className="text-end">{formatAmount(payout.amount, payout.currency)}</td>
                      <td className="text-end">{formatAmount(payout.fee, payout.currency)}</td>
                      <td className="text-end" style={{ fontWeight: '600' }}>
                        {formatAmount(payout.netAmount, payout.currency)}
                      </td>
                      <td>{payout.paymentMethod || 'N/A'}</td>
                      <td>
                        <span className={`badge ${
                          payout.status === 'completed' ? 'bg-success' :
                          payout.status === 'failed' ? 'bg-danger' :
                          'bg-warning'
                        }`}>
                          {payout.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Payout for {selectedOrg?.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPayoutModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmitPayout}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <strong>Outstanding Balance:</strong> {formatAmount(selectedOrg?.payouts.outstanding, selectedOrg?.currency)}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Payout Amount *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={payoutForm.amount}
                      onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                      max={selectedOrg?.payouts.outstanding}
                      step="0.01"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Fee/Charges</label>
                    <input
                      type="number"
                      className="form-control"
                      value={payoutForm.fee}
                      onChange={(e) => setPayoutForm({ ...payoutForm, fee: e.target.value })}
                      step="0.01"
                    />
                  </div>

                  <div className="alert alert-secondary">
                    <strong>Net Amount (paid to org):</strong> {formatAmount(netAmount, selectedOrg?.currency)}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Payment Method *</label>
                    <select
                      className="form-control"
                      value={payoutForm.paymentMethod}
                      onChange={(e) => setPayoutForm({ ...payoutForm, paymentMethod: e.target.value })}
                      required
                    >
                      <option value="">Select method...</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Mobile Money">Mobile Money</option>
                      <option value="Cash">Cash</option>
                      <option value="Check">Check</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Reference/Transaction ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={payoutForm.reference}
                      onChange={(e) => setPayoutForm({ ...payoutForm, reference: e.target.value })}
                      placeholder="e.g., TXN123456"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      value={payoutForm.notes}
                      onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                      rows="3"
                      placeholder="Any additional notes..."
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPayoutModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Processing...' : 'Create Payout'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts;
